import { spacing } from '@scality/core-ui';
import { fontFamily } from '@scality/core-ui/dist/style/theme';
import styled from 'styled-components';

const SLIDE = '600ms ease';

const GUTTER_MIN = spacing.r12;
const GUTTER_SHARE = '2.5%';

export const SplitRoot = styled.div<{
  $open: boolean;
  $animate: boolean;
  $ratio: number;
}>`
  box-sizing: border-box;
  display: grid;
  grid-template-areas: 'panel handle chat';
  grid-template-columns: ${(props) =>
    props.$open ? `minmax(0, ${props.$ratio}fr) auto minmax(0, ${1 - props.$ratio}fr)` : `0fr auto minmax(0, 1fr)`};
  flex: 1;
  min-width: 0;
  height: 100%;
  min-height: 0;
  position: relative;
  /* Transitions start from the after-change style, so switching this off suppresses the slide for
     the value committed alongside it — see PanelStore.animateLayout and DualPanelLayout. */
  transition: ${(props) => (props.$animate ? `grid-template-columns ${SLIDE}` : 'none')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** The drag handle: a 1px line to look at, in a 9px strip to aim at. */
export const SplitHandle = styled.div<{ $dragging: boolean }>`
  grid-area: handle;
  position: relative;
  width: 9px;
  cursor: col-resize;
  flex: 0 0 auto;
  background: none;
  touch-action: none;
  z-index: 2;
  margin-top: var(--header-height);

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 4px;
    width: 1px;
    background-color: ${(props) => (props.$dragging ? props.theme.selectedActive : props.theme.border)};
    transition: background-color 120ms ease;
  }

  /* The grip: the hairline alone reads as a border, not as something you can move. */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 3px;
    width: 3px;
    transform: translateY(-50%);
    height: ${spacing.r32};
    border-radius: 2px;
    background-color: ${(props) => (props.$dragging ? props.theme.selectedActive : props.theme.textSecondary)};
    transition: background-color 120ms ease;
    z-index: 1;
  }

  &:hover::before,
  &:focus-visible::before {
    background-color: ${(props) => props.theme.selectedActive};
  }

  &:hover::after,
  &:focus-visible::after {
    background-color: ${(props) => props.theme.selectedActive};
    width: 2px;
  }

  &:focus-visible {
    outline: none;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after,
    &::before {
      transition: none;
    }
  }
`;

export const ChatColumn = styled.div<{ $hasPanels: boolean }>`
  grid-area: chat;
  /* Beside a panel the column is narrow, so the reading gutter shrinks to a plain margin. The
     formula is restated rather than composed — see the note in index.css. */
  ${(props) =>
    props.$hasPanels &&
    `
    --chat-gutter: max(${spacing.r16}, calc((100% - var(--chat-max-width)) / 2));
  `}
  min-width: 0;
  display: flex;
  overflow: hidden;
`;

export const MainPanelColumn = styled.div<{ $open: boolean }>`
  grid-area: panel;
  min-width: 0;
  display: flex;
  overflow: hidden;
  /* Full height — the panel fills the viewport edge to edge, not a floating card. */
  height: 100%;
`;

export const MainPanelRoot = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  padding-top: var(--header-height);
  background-color: ${(props) => props.theme.backgroundLevel4};
  color: ${(props) => props.theme.textPrimary};
`;

export const MainPanelBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.r16};
  padding: ${spacing.r16} max(${GUTTER_MIN}, ${GUTTER_SHARE});
  overflow-y: auto;
  min-height: 0;
`;

/* Collapsed-panels handle: a vertical tab flush against the rail (SplitRoot already starts after
   the rail), out of the grid flow. Clicking it re-opens the panel column. */
export const CollapsedPanelTab = styled.button`
  position: absolute;
  top: calc(var(--header-height) + ${spacing.r24});
  left: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.r12};
  margin: 0;
  padding: ${spacing.r16} ${spacing.r8};
  border: 1px solid ${(props) => props.theme.border};
  border-left: none;
  border-top-right-radius: ${spacing.r8};
  border-bottom-right-radius: ${spacing.r8};
  background-color: ${(props) => props.theme.backgroundLevel2};
  color: ${(props) => props.theme.textSecondary};
  /* A native <button> does not inherit font-family: the UA stylesheet gives it its own
     (Arial in Chromium), and setting font-size or font-weight does not bring the family
     back. Its label span then inherits Arial from here. Take the family from the library
     rather than restating it. */
  font-family: ${fontFamily};
  cursor: pointer;
  transition: color 150ms ease, background-color 150ms ease;

  &:hover {
    color: ${(props) => props.theme.textPrimary};
    background-color: ${(props) => props.theme.backgroundLevel3};
  }
`;

export const CollapsedPanelTabLabel = styled.span`
  writing-mode: vertical-rl;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
`;

export const CollapsedPanelTabBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 ${spacing.r4};
  border-radius: 999px;
  background-color: ${(props) => props.theme.infoPrimary};
  color: ${(props) => props.theme.textPrimary};
  font-size: 0.75rem;
  font-weight: 600;
`;

/* --- Header + tab strip --- */

export const MainPanelHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${spacing.r8};
  padding-right: ${spacing.r8};
  border-bottom: 1px solid ${(props) => props.theme.border};
  flex: 0 0 auto;
`;

export const PanelTabBar = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${spacing.r4};
  padding: ${spacing.r8} ${spacing.r8} 0;
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
`;

/** A lone panel's name. Padded like a tab label, so the header doesn't shift when tabs return. */
export const PanelHeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.r8};
  flex: 1 1 auto;
  min-width: 0;
  padding: ${spacing.r8} ${spacing.r12};
  color: ${(props) => props.theme.textPrimary};
  font-weight: 600;
`;

export const PanelTab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.r8};
  max-width: 12rem;
  padding: ${spacing.r8} ${spacing.r12};
  border: 1px solid ${(props) => props.theme.border};
  border-bottom: none;
  border-top-left-radius: ${spacing.r4};
  border-top-right-radius: ${spacing.r4};
  background-color: ${(props) => (props.$active ? props.theme.backgroundLevel1 : 'transparent')};
  color: ${(props) => (props.$active ? props.theme.textPrimary : props.theme.textSecondary)};
  font-family: ${fontFamily};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: ${(props) => props.theme.textPrimary};
  }
`;

export const PanelTabLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PanelTabInput = styled.input`
  min-width: 0;
  max-width: 10rem;
  border: none;
  outline: none;
  padding: 0;
  background: transparent;
  color: ${(props) => props.theme.textPrimary};
  font: inherit;
`;

export const PanelTabClose = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  padding: ${spacing.r2};
  color: ${(props) => props.theme.textSecondary};

  &:hover {
    color: ${(props) => props.theme.textPrimary};
    background-color: ${(props) => props.theme.backgroundLevel3};
  }
`;
